import { h, Component } from 'preact';
import Quill from 'quill';
import Store from 'store/store';
import { joinClasses } from 'utils/utils';
import * as jsonPointer from 'utils/jsonPointer';

import FieldBase from 'components/editor/settings/fieldBase';

import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import s from 'components/editor/settings/richTextField.sss';

const Delta = Quill.import('delta');
const Embed = Quill.import('blots/embed');

class BreakBlot extends Embed {
	static blotName = 'break';
	static tagName = 'BR';
}
// Register a custom Break blot for soft line breaks (<br>) within paragraphs
Quill.register(BreakBlot);

const inlineFormats = ['size', 'bold', 'italic', 'underline', 'strike', 'link', 'code'];
const blockFormats = ['header', 'list', 'blockquote', 'align', 'code-block'];
const embedFormats = ['image', 'video'];

export default class RichTextField extends FieldBase {
	s = s;

	constructor(props) {
		super(props);
		this.onInput = this.onInput.bind(this);
	}

	getHelp() {
		return this.props.help ?? 'Press Shift+Enter to insert a line break';
	}

	onInput() {
		this.props.onInput?.({ target: this.editor });
		if (!this.props.path || !this.editor) return;
		jsonPointer.setDeep(Store.get().data, this.props.path, this.editor.value);
	}

	renderField() {
		return (
			<RichTextInput
				{...this.props}
				class={s.field}
				value={this.value}
				onInput={this.onInput}
				readonly={this.readonly}
				disabled={this.disabled}
				ref={e => this.editor = e}
			/>
		);
	}
}

// Transforms delta in-place and returns it
function removeNewlinesFromDelta(delta) {
	if (!delta?.ops) return;
	delta.ops.forEach(op => {
		if (typeof op?.insert === 'string') {
			op.insert = op.insert.replace(/\r?\n/g, ' ');
		}
	});
	return delta;
}

const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1);
const tooltips = {
	color: 'Text color',
	clean: 'Clear formatting',
};
// const shortcutTooltips = {
// 	bold: '[short]+b',
// 	italic: '[short]+i',
// 	underline: '[short]+u',
// };
// const SHORTKEY = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform) ? 'cmd' : 'ctrl';
class RichTextInput extends Component {
	constructor(props) {
		super(props);
		this.onTextChange = this.onTextChange.bind(this);
		this.fullscreen = this.fullscreen.bind(this);
	}

	get name() {
		return this.props.name;
	}

	// Quill always keeps a trailing newline (2 char), so a length of 2 means the document is empty
	get isEmpty() {
		return !this.quill || this.quill.getLength() <= 2;
	}

	get value() {
		if (this.isEmpty) return '';
		let html = this.quill.getSemanticHTML();
		// Temporary fix for quill outputting all spaces as non-breaking spaces - see https://github.com/slab/quill/issues/4509
		html = html.replaceAll(/((?:&nbsp;)*)&nbsp;/g, '$1 ');
		// Remove trailing newlines, quill has a weird thing where it adds an extra one each time. Realistically, unlikely that trailing newlines are intended.
		html = html.replace(/(<br>)+<\/p>$/, '</p>');
		if (this.props.inline) {
			// Removes wrapping p tag (does not do anything if there are multiple, but that hopefully should not be possible in inline mode)
			html = html.replace(/^<p>(?!.*<p>)(.*)<\/p>$/i, '$1');
		}
		return html;
	}

	set value(value) {
		if (!this.quill) return;
		// this.quill.root.innerHTML = value || '';

		const selection = this.quill.getSelection();
		// Using clipboard.convert to handle newline removal for inline stuff
		const delta = this.quill.clipboard.convert({ html: value || '' });
		this.quill.setContents(delta);

		// Restore cursor position? By default it puts it at the beginning
		// Or should we put it at the end, or try to be smarter about it?
		if (selection) {
			this.quill.setSelection(selection);
		}
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.value !== this.props.value && nextProps.value !== this.value) {
			this.value = nextProps.value;
		}
		if (nextProps.style !== this.props.style) {
			this.applyStyle(nextProps.style);
		}
	}

	applyStyle(style) {
		if (!style || !this.base) return;
		Object.entries(style).forEach(([key, value]) => this.base.style.setProperty(key, value));
	}

	shouldComponentUpdate() {
		return false;
	}

	componentDidMount() {
		const formats = this.getFormats();
		this.quill = new Quill(this.$editor, {
			theme: this.props.floating ? 'bubble' : 'snow',
			formats,
			modules: {
				toolbar: { container: this.getToolbarOptions(formats), handlers: { fullscreen: this.fullscreen } },
				keyboard: { bindings: this.getKeyBindings() },
			},
			readOnly: this.props.readonly,
		});
		// Ensure style is applied (CSS variables need setProperty)
		this.applyStyle(this.props.style);
		// Add a clipboard matcher to handle <br> tags when pasting or setting HTML content
		this.quill.clipboard.addMatcher('BR', (node, delta) => {
			return new Delta().insert({ break: true });
		});
		this.addTooltips();
		this.handleInline();
		this.value = this.props.value ?? this.props.defaultValue;
		if (this.props.onInput) {
			this.quill.on('text-change', this.onTextChange);
		}
	}

	componentWillUnmount() {
		if (!this.quill) return;
		this.quill.enable(false);
		this.quill.emitter.removeAllListeners();
		this.$editor.className = '';
	}

	onTextChange(delta, oldContents, source) {
		// Only trigger an "input" event for user-initiated changes to match the HTML input events
		if (source === 'user') {
			this.props.onInput?.({ target: this });
		}
	}

	getFormats() {
		return this.props.formats || [
			...inlineFormats,
			'break',
			!this.props.inline && blockFormats,
			!this.props.inline && this.props.embeds && embedFormats,
		].filter(Boolean).flat();
	}

	getToolbarOptions(formats = this.getFormats()) {
		const allowedOptions = formats.concat('clean', 'fullscreen');

		// TODO: update handler for image to use our media library instead of base64
		const toolbarOptions = [
			[{ header: [1, 2, 3, 4, false] }],
			['bold', 'italic', 'underline', 'strike'],
			// [{ color: [] }],
			[{ align: [false, 'center', 'right'] }], // [{ list: 'ordered' }, { list: 'bullet' }, { align: [] }],
			['link', 'image'],
			['clean'],
			['fullscreen'],
		].map(
			a => a.filter(
				f => f && allowedOptions.includes(typeof f === 'string' ? f : Object.keys(f)[0])
			)
		).filter(
			e => Array.isArray(e) ? e.length : e
		);

		return toolbarOptions;
	}

	getKeyBindings() {
		// Do not capture tab for accessibility (we could change this to handle regular tab to insert the character vs escape then tab to move focus)
		const bindings = { tab: null };

		if (this.props.inline) {
			bindings['no-newline'] = {
				key: 'Enter',
				handler: () => false,
			};
		} else {
			// Shift+Enter inserts a soft line break (<br>) within the same paragraph
			// Regular Enter creates a new paragraph (<p>)
			bindings['shift_enter'] = {
				key: 'Enter',
				shiftKey: true,
				handler: (range) => {
					this.quill.insertEmbed(range.index, 'break', true, 'user');
					this.quill.setSelection(range.index + 1, 'silent');
					return false;
				},
			};
		}

		return bindings;
	}

	addTooltips() {
		const dropdowns = ['color', 'align'];
		this.base.querySelectorAll('.ql-toolbar :is(button, [role="button"])').forEach(button => {
			let label = button.getAttribute('aria-label');
			if (!label) {
				label = dropdowns.find(type => button.parentElement.classList.contains(`ql-${type}`));
				if (!label) return;
			}
			let title = tooltips[label] || capitalize(label);
			// let shortcut = shortcutTooltips[label];
			// if (shortcut) shortcut = `<${shortcut.replace(/\[short\]/g, SHORTKEY)}>`;
			// button.title = [title, shortcut].filter(Boolean).join(' ');
			button.title = title;
		});
	}

	handleInline() {
		// NOTE: We disable the enter key and remove pasted newlines
		//   Is it good enough, or is there any other way to input newlines (e.g. dictation) ?
		if (!this.props.inline) return;

		// Hacky... there seems to be no easy way to override default matchers https://github.com/slab/quill/blob/v2.0.3/packages/quill/src/modules/clipboard.ts#L89-L93
		//   (and with function name mangling on prod builds, we can't easily know which matcher is which in this.quill.clipboard.matchers)
		//   so we override the function to replace newlines once everything has been processed
		this.quill.clipboard.convert = this.overrideClipboardConvertToRemoveNewlines(this.quill.clipboard.convert);
	}

	overrideClipboardConvertToRemoveNewlines(baseFunction) {
		return function() {
			return removeNewlinesFromDelta(baseFunction.apply(this, arguments));
		};
	}

	fullscreen() {
		if (document.fullscreenElement) {
			document.exitFullscreen?.();
			return;
		}
		this.base.requestFullscreen?.({ navigationUI: 'show' });
	}

	render(props) {
		const autosize = props.inline || props.rows === 'auto';
		const rows = autosize ? undefined : props.rows;
		const style = `--editor-rows: ${rows || 'initial'}`;
		return (
			<div class={joinClasses(s.editor, autosize && s.autosize, props.dark && s.dark, props.class)} style={style}>
				<div ref={e => this.$editor = e} />
			</div>
		);
	}
}